import { z } from "zod";
import {
  legacyMerchTerritorySchema,
  merchStoresByRegionSchema,
  merchTerritorySchema,
} from "./merch-territory";
import { MUSIC_TYPE_OPTIONS } from "@libs/utils";
import { eventKindSchema } from "./event-kind";
import { eventStartLocalSchema } from "./event-time";
import { fanClubSchema } from "@libs/fan-club-contracts";

export const artistLinkPageSocialTypes = [
  "website",
  "instagram",
  "facebook",
  "tiktok",
  "youtube",
  "x",
  "spotify",
  "appleMusic",
  "bandcamp",
  "patreon",
  "soundcloud",
] as const;

export const artistLinkPageSlugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const httpUrlSchema = z
  .string()
  .url()
  .refine(
    (value) => {
      const protocol = new URL(value).protocol;
      return protocol === "http:" || protocol === "https:";
    },
    { message: "URL must use http or https" },
  );
const nullableHttpUrlSchema = httpUrlSchema.nullable();

/**
 * Icon a header social link renders with. Brand icons mirror the known social
 * `type`s; the generic icons (`globe`, `link`, `email`, `music`) cover custom
 * header entries that are not a known brand. Must stay in sync with
 * `HEADER_SOCIAL_ICONS` in `bio-config.ts`.
 */
export const artistLinkPageSocialIcons = [
  "instagram",
  "tiktok",
  "spotify",
  "appleMusic",
  "youtube",
  "x",
  "facebook",
  "bandcamp",
  "patreon",
  "soundcloud",
  "website",
  "globe",
  "link",
  "email",
  "music",
] as const;
export type ArtistLinkPageSocialIcon =
  (typeof artistLinkPageSocialIcons)[number];

export const artistLinkPageSocialLinkSchema = z.object({
  label: z.string().min(1),
  url: z.string().url(),
  icon: z.enum(artistLinkPageSocialIcons),
});

export const artistLinkPageThemeSchema = z.object({
  accent: z.string().min(1),
  buttonShape: z.enum(["rounded", "pill", "square"]),
  background: z.string().min(1),
  animation: z.enum(["none", "fade", "float", "gradient"]),
  effect: z
    .enum([
      "none",
      "snow",
      "rain",
      "leaves",
      "petals",
      "fireflies",
      "stars",
      "falling-stars",
      "cascade",
      "smoke",
      "bubbles",
      "confetti",
      "hearts",
    ])
    .optional(),
  embedBackground: z.string().min(1).optional(),
  /** Text painted on the background; absent picks light or dark to read on it. */
  textColor: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i)
    .optional(),
});

export const artistLinkPageSectionStyleSchema = z.object({
  background: z.string().min(1),
  stroke: z.string().min(1),
  textColor: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i)
    .optional(),
  buttonBackground: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i)
    .optional(),
  buttonTextColor: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i)
    .optional(),
  iconColor: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i)
    .optional(),
  iconBackground: z
    .union([z.string().regex(/^#[0-9a-f]{6}$/i), z.literal("transparent")])
    .optional(),
  buttonStroke: z
    .union([z.string().regex(/^#[0-9a-f]{6}$/i), z.literal("transparent")])
    .optional(),
  buttonShape: z.enum(["rounded", "pill", "square"]).optional(),
  font: z.enum(["default", "serif", "mono"]),
});

const artistLinkPageSectionPresentationSchema = z.object({
  label: z.string().min(1).nullable().optional(),
  hideLabel: z.boolean().optional(),
  layout: z.enum(["list", "grid", "carousel"]).optional(),
  style: artistLinkPageSectionStyleSchema.optional(),
});

export const artistLinkPageSongPlatformTypes = [
  "lissen",
  "spotify",
  "appleMusic",
  "deezer",
  "youtubeMusic",
  "tidal",
  "boomplay",
  "audiomack",
] as const;

export const artistLinkPageSongPlatformLinkSchema = z.object({
  type: z.enum(artistLinkPageSongPlatformTypes),
  label: z.string().min(1),
  url: z.string().url(),
});

export const artistLinkPageAccessSchema = z.object({
  checkoutJourney: z.literal("buy_on_behalf").optional(),
  type: z.literal("event").default("event"),
  id: z.string().min(1),
  title: z.string().min(1),
  kind: eventKindSchema.nullable().optional(),
  // start INSTANT — logic/sorting only; display comes from startsAtLocal (venue-local
  // components resolved server-side — clients do zero timezone work).
  startsAt: z.string().datetime(),
  startsAtLocal: eventStartLocalSchema,
  endsAt: z.string().datetime().nullable(),
  // 2026-07-22 partner-profile rework: converger-created event accesses (rinse radio
  // episodes, partner gigs) legitimately carry no venue/city/country — one such event
  // in an artist's concerts section must not 500 the whole public bio page. READ
  // schema: accept empty; render layers guard/omit.
  city: z.string(),
  venue: z.string(),
  country: z.string(),
  imageUrl: z.string().url().nullable(),
  ticketUrl: z.string().url().nullable(),
  accessUrl: z.string().min(1).nullable(),
  // This concert's ticket button; each set field replaces the section's value.
  ticketButton: z
    .object({
      label: z.string().min(1).optional(),
      background: z
        .string()
        .regex(/^#[0-9a-f]{6}$/i)
        .optional(),
      textColor: z
        .string()
        .regex(/^#[0-9a-f]{6}$/i)
        .optional(),
      stroke: z
        .union([z.string().regex(/^#[0-9a-f]{6}$/i), z.literal("transparent")])
        .optional(),
      shape: z.enum(["rounded", "pill", "square"]).optional(),
    })
    .optional(),
  ticketReward: z
    .discriminatedUnion("type", [
      z.object({
        type: z.literal("price"),
        originalAmount: z.number().nonnegative(),
        amount: z.number().nonnegative(),
        currency: z.string().min(1),
      }),
      z.object({
        type: z.literal("label"),
        label: z.string().trim().min(1),
      }),
    ])
    .nullable()
    .default(null),
  price: z
    .object({
      amount: z.number().nonnegative(),
      currency: z.string().min(1),
      /** The lowest of several ticket prices: render as "From …". */
      from: z.boolean().optional(),
    })
    .nullable()
    .default(null),
  /** Its 1-tap scan is queued or loading: the page reads `one-tap-status`
   *  until it ends, and shows it as 1-tap in place if it becomes one. */
  oneTapPending: z.literal(true).optional(),
});

/**
 * `GET /platform/link-pages/:slug/one-tap-status?ids=…` (and `POST
 * …/one-tap-scan`, when a fan opens the Events "See all"): which of the
 * events asked about 1-tap now sells, with the fields that change when it
 * does, as the page would serve them; and whether a scan of the artist's
 * events is still running.
 */
export const oneTapStatusSchema = z.object({
  scanning: z.boolean(),
  accesses: z.array(
    artistLinkPageAccessSchema.pick({
      id: true,
      accessUrl: true,
      checkoutJourney: true,
      price: true,
      startsAt: true,
      startsAtLocal: true,
      endsAt: true,
    }).partial({
      // Older status responses predate scans taking the seller's date.
      startsAt: true,
      startsAtLocal: true,
      endsAt: true,
    }).extend({
      /** Remove this event from a cached or open bio list. Older readers
       *  ignore the additive field and keep their redirect until a reload. */
      hidden: z.boolean().optional(),
    }),
  ),
});
export type OneTapStatus = z.infer<typeof oneTapStatusSchema>;

export const artistLinkPageMusicAccessSchema = z.object({
  type: z.literal("music"),
  id: z.string().min(1),
  title: z.string().min(1),
  musicAccessType: z.enum(MUSIC_TYPE_OPTIONS),
  imageUrl: z.string().url().nullable(),
  accessUrl: z.string().min(1).nullable(),
  price: z
    .object({
      amount: z.number().nonnegative(),
      currency: z.string().min(1),
    })
    .nullable()
    .default(null),
  songs: z
    .array(
      z.object({
        id: z.string().min(1),
        title: z.string().min(1),
        artworkUrl: z.string().url().nullable(),
      }),
    )
    .min(1),
});

/** A promoted access may be an event or a music unlock. */
export const artistLinkPagePinnedAccessSchema = z.union([
  artistLinkPageMusicAccessSchema,
  artistLinkPageAccessSchema,
]);

/**
 * A merch access as a Merch section's card shows it. A product on sale
 * through 1-tap has its price: the cheapest size at quantity one without
 * UK delivery (`merchHeadline`), and the UK delivery that size costs, which
 * the card says beside the price (none where the store quotes its own); a
 * price before its seller's tax with "+ local tax" (`taxAdded`). The card
 * opens the product's access page (`accessUrl`) in the same sheet as a
 * concert. A product the artist picked that is sold out (`soldOut`) is
 * served too, in its place, at the price its sizes last sold at.
 */
export const artistLinkPageMerchSchema = z.object({
  type: z.literal("merch"),
  id: z.string().min(1),
  title: z.string().min(1),
  imageUrl: z.string().url().nullable(),
  accessUrl: z.string().min(1),
  price: z.object({
    amount: z.number().nonnegative(),
    currency: z.string().min(1),
    /** Sizes at different prices: render as "From …". */
    from: z.boolean().optional(),
  }),
  /** The UK delivery of the cheapest size, in minor units of `price`; null
   *  where the store quotes its own for each basket in the purchase sheet
   *  (a Shopify store's): "+ UK delivery". */
  deliveryMinor: z.number().int().nonnegative().nullable(),
  /** A digital download: the store emails it, so its card says "Digital
   *  download" where a posted product says its delivery. Absent: posted.
   *  Not strict, so a bundle without it drops it. */
  download: z.literal(true).optional(),
  /** Its seller adds tax at checkout for the page's fan (LIS-1133): `price`
   *  is before tax, and the card says "+ local tax" after it
   *  (`merchPriceLabel`); the checkout asks the fan to confirm the total
   *  with the tax before Pay. Absent: the price includes any tax. */
  taxAdded: z.literal(true).optional(),
  /** Sold out: every size of the fan's listing is sold out. Only an
   *  artist's pick is served so (Auto adds only products on sale); its
   *  card shows its picture, title and `price` (the cheapest size's last
   *  price) and a disabled "Sold out" in place of Buy, and opens nothing.
   *  `deliveryMinor` is null. Absent: on sale. An older build, which does
   *  not know it, draws a Buy whose sheet finds every size sold out. */
  soldOut: z.literal(true).optional(),
  /** The regions it is on sale to (`MERCH_TERRITORIES`): where one of its
   *  listings sells and delivers to a country of the region. The page and
   *  the editors pick a region's products by it. Absent from an older
   *  server: its currency says (`merchTerritoriesOfCurrency`). */
  regions: z.array(merchTerritorySchema).optional(),
  /** The stores it sells from in each region (`MerchStoresByRegion`), so
   *  Auto shows a product of each. Absent from an older server: Auto
   *  ranks by age alone. */
  stores: merchStoresByRegionSchema.optional(),
  /** Legacy dollar listings measured only for GB, with no market map.
   *  Preserve their currency reach without inferring a new sale or price. */
  regionFallback: z.literal("north_america").optional(),
});

export const artistLinkPageTourSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  /** The group's word on its card ("Festival"); absent means "Tour". */
  label: z.string().min(1).optional(),
  imageUrl: nullableHttpUrlSchema,
  accesses: z.array(artistLinkPageAccessSchema).min(1),
});

export const artistLinkPageConcertItemSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("access"),
    access: artistLinkPageAccessSchema,
  }),
  z.object({
    type: z.literal("tour"),
    tour: artistLinkPageTourSchema,
  }),
]);

export const artistLinkPagePopularSongSchema = z.object({
  id: z.string().min(1),
  // Active first-party music access containing this song, when one exists for
  // the page artist. Viewer ownership is intentionally resolved separately so
  // the public page payload remains edge-cacheable.
  musicAccessId: z.string().min(1).nullable().optional(),
  // The canonical album route needed to open this song in the app. Nullable
  // so payloads produced before this field existed (edge caches, deploy skew)
  // still parse — the song then simply renders without an app link.
  albumId: z.string().min(1).nullable().default(null),
  title: z.string().min(1),
  artistName: z.string().min(1),
  artworkUrl: z.string().url().nullable(),
  durationSeconds: z.number().nonnegative(),
  // Public HLS manifest URL; the CDN serves only the preview window without
  // a signed token, so exposing it on an unauthenticated endpoint is safe.
  previewUrl: z.string().url().nullable(),
  platformLinks: z.array(artistLinkPageSongPlatformLinkSchema),
  // Present while the song is unreleased, with its release date
  // (`YYYY-MM-DD`). On the public page every platform link is then the
  // song's pre-save link and the song has no playback.
  preSave: z.object({ releaseDate: z.iso.date() }).optional(),
});

/**
 * A resolved album in the Songs section. An album renders as a collapsible row
 * (artwork + title + artist; expands to reveal `tracks` and the album platform
 * links pill). `platformLinks` reuses the song platform link schema (album
 * identifiers, manual-over-system, plus the Lissen deep link).
 */
export const artistLinkPageAlbumSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  artworkUrl: z.string().url().nullable(),
  artistName: z.string().min(1),
  platformLinks: z.array(artistLinkPageSongPlatformLinkSchema),
  tracks: z.array(artistLinkPagePopularSongSchema),
});

/**
 * A single ordered item in the Songs section: either a song or an album.
 * Discriminated on `type`. (Collaborator stays songs-only.)
 */
export const artistLinkPageSongItemSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("song"),
    song: artistLinkPagePopularSongSchema,
  }),
  z.object({
    type: z.literal("album"),
    album: artistLinkPageAlbumSchema,
  }),
]);

export const artistLinkPageCustomLinkSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  url: httpUrlSchema,
  imageUrl: nullableHttpUrlSchema,
  fansOnly: z.boolean().optional(),
});

export const artistLinkPageFanStatusSchema = z.object({
  isFan: z.boolean(),
});

export const artistLinkPageArtistSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  imageUrl: z.string().url().nullable(),
  faviconUrl: z.string().url().nullable().optional(),
  socialPreviewTitle: z.string().nullable(),
  socialPreviewDescription: z.string().nullable(),
});

/**
 * Public wire payload sections, in render order. The shared renderer maps over
 * `sections` directly. A section is included ONLY if it was `enabled` in config
 * AND resolved to ≥1 item — empty sections are omitted on the public page.
 *
 * See `docs/contexts/link-in-bio/reference/editor.md`.
 */
export const artistLinkPageHeaderSectionSchema = z.object({
  type: z.literal("header"),
  data: z.object({
    socialLinks: z.array(artistLinkPageSocialLinkSchema),
    /** How the page name is shown; absent fields use the renderer defaults. */
    name: z
      .object({
        hidden: z.boolean().optional(),
        font: z.enum(["default", "serif", "mono"]).optional(),
        size: z.enum(["small", "medium", "large"]).optional(),
        color: z
          .string()
          .regex(/^#[0-9a-f]{6}$/i)
          .optional(),
      })
      .optional(),
  }),
});

export const artistLinkPageSongsSectionSchema =
  artistLinkPageSectionPresentationSchema.extend({
    type: z.literal("songs"),
    data: z.object({
      items: z.array(artistLinkPageSongItemSchema),
    }),
  });

export const artistLinkPageConcertsSectionSchema =
  artistLinkPageSectionPresentationSchema.extend({
    type: z.literal("concerts"),
    // Replaces the default "Tickets" label on the section's ticket buttons.
    buttonLabel: z.string().min(1).optional(),
    data: z.object({
      // Kept for older clients. New renderers prefer `items`, which preserves
      // the configured interleaving of standalone concerts and tour groups.
      accesses: z.array(artistLinkPageAccessSchema),
      items: z.array(artistLinkPageConcertItemSchema).optional(),
      /** Auto has more upcoming events than the section shows (its Max
       *  items): See all reads them (`/link-pages/:slug/sections/concerts`). */
      more: z.literal(true).optional(),
    }),
  });

export const artistLinkPageMerchSectionSchema =
  artistLinkPageSectionPresentationSchema.extend({
    type: z.literal("merch"),
    // Replaces the default "Buy" label on the section's buy buttons.
    buttonLabel: z.string().min(1).optional(),
    data: z.object({
      items: z.array(artistLinkPageMerchSchema),
      /** What fans in each region see instead of `items` (All markets):
       *  its own list, or what it gets of the main picks. Empty where
       *  nothing is offered; optional for responses from older servers. */
      regions: z
        .partialRecord(merchTerritorySchema, z.array(artistLinkPageMerchSchema))
        .optional(),
      /** North America's of `regions`, for page bundles older than the
       *  regions, which refuse a key they do not know. */
      territories: z
        .partialRecord(legacyMerchTerritorySchema, z.array(artistLinkPageMerchSchema))
        .optional(),
      /** Auto has more products than the section shows (its Max items):
       *  See all reads them (`/link-pages/:slug/sections/merch`). */
      more: z.literal(true).optional(),
    }),
  });

export const artistLinkPageLinksSectionSchema =
  artistLinkPageSectionPresentationSchema.extend({
    type: z.literal("links"),
    id: z.string().min(1).optional(),
    data: z.object({
      links: z.array(artistLinkPageCustomLinkSchema),
    }),
  });

export const artistLinkPageCollaboratorSectionSchema =
  artistLinkPageSectionPresentationSchema.extend({
    type: z.literal("collaborator"),
    data: z.object({
      songs: z.array(artistLinkPagePopularSongSchema),
    }),
  });

export const artistLinkPageBookingSectionSchema =
  artistLinkPageSectionPresentationSchema.extend({
    type: z.literal("booking"),
    data: z.object({
      title: z.string().min(1).max(80),
      minimumPrice: z.number().finite().nonnegative(),
      currency: z.string().regex(/^[A-Z]{3}$/),
      /** The card's image; absent: the calendar icon. */
      imageUrl: httpUrlSchema.optional(),
    }),
  });

export const ARTIST_MEDIA_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;
export const artistMediaUploadMimeTypes = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "video/mp4",
  "video/quicktime",
  "video/webm",
] as const;

export const artistLinkPageMediaUploadSectionSchema =
  artistLinkPageSectionPresentationSchema.extend({
    type: z.literal("mediaUpload"),
    data: z.object({
      title: z.string().min(1).max(80),
      description: z.string().min(1).max(240),
      buttonLabel: z.string().min(1).max(40),
      acceptedMimeTypes: z.array(z.enum(artistMediaUploadMimeTypes)).min(1),
      maxFileSizeBytes: z.number().int().positive(),
    }),
  });

export const artistLinkPageMailingListSectionSchema =
  artistLinkPageSectionPresentationSchema.extend({
    type: z.literal("mailingList"),
    data: z.object({
      title: z.string().min(1).max(80),
      description: z.string().max(240),
      emailPlaceholder: z.string().min(1).max(80),
      buttonLabel: z.string().min(1).max(40),
      successMessage: z.string().min(1).max(120),
      /**
       * Only the email field and its button are drawn: no icon, title or
       * description. Set by an artist site's own sign-up.
       */
      formOnly: z.boolean().optional(),
    }),
  });

/** The artist's open fan club and its plans (LIS-1156). */
export const artistLinkPageClubSectionSchema =
  artistLinkPageSectionPresentationSchema.extend({
    type: z.literal("club"),
    data: fanClubSchema,
  });

/**
 * A bundle (LIS-1250): one of the artist's 1tap events and one of its 1tap
 * merch products, bought together, both or neither. Served only while the
 * page's fan can get both: the event on sale through 1tap from a seller the
 * spine runs (its ticket pays after the merch), the merch on sale. Each
 * part is the card its own section would serve.
 */
export const artistLinkPageBundleSchema = z.object({
  id: z.string().min(1),
  /** Its name on the page ("Bristol + Flaming Buckfast Tee"). */
  title: z.string().min(1),
  /** The short label above its name ("TICKET + TEE"), or none. */
  tag: z.string().min(1).nullable(),
  event: artistLinkPageAccessSchema,
  merch: artistLinkPageMerchSchema,
});

/** The artist's bundles the page's fan can get, newest first (LIS-1250). */
export const artistLinkPageBundlesSectionSchema =
  artistLinkPageSectionPresentationSchema.extend({
    type: z.literal("bundles"),
    data: z.object({ bundles: z.array(artistLinkPageBundleSchema).min(1) }),
  });

export const artistLinkPageSectionSchema = z.discriminatedUnion("type", [
  artistLinkPageHeaderSectionSchema,
  artistLinkPageSongsSectionSchema,
  artistLinkPageBundlesSectionSchema,
  artistLinkPageConcertsSectionSchema,
  artistLinkPageMerchSectionSchema,
  artistLinkPageLinksSectionSchema,
  artistLinkPageBookingSectionSchema,
  artistLinkPageMediaUploadSectionSchema,
  artistLinkPageMailingListSectionSchema,
  artistLinkPageCollaboratorSectionSchema,
  artistLinkPageClubSectionSchema,
]);

export const BOOKING_CURRENCIES = ["GBP", "EUR", "USD", "CAD", "AUD"] as const;

/** Action-token context for a booking request on one artist page. */
export const bookingIdentityContext = (artistSlug: string) =>
  `booking:${artistSlug.trim().toLowerCase()}`;

export const artistBookingRequestSchema = z
  .object({
    // Either a typed address or an identity action token (purpose `booking`),
    // from which the platform takes the address.
    email: z.string().trim().email().max(254).optional(),
    identityToken: z.string().min(1).max(4096).optional(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    location: z.string().trim().min(2).max(200),
    price: z.number().finite().nonnegative(),
    currency: z
      .string()
      .trim()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    note: z.string().trim().max(2_000),
    // Honeypot: real visitors never see or populate this field.
    website: z.string().max(0).optional(),
  })
  .refine((request) => !!request.email || !!request.identityToken, {
    message: "email or identityToken is required",
    path: ["email"],
  });

export const artistBookingRequestResponseSchema = z.object({
  success: z.literal(true),
});

export const artistAudienceSourceSchema = z.enum([
  "live_link_signup",
  "mobile_app_signup",
  "pinned_pre_release",
  "concert_purchase",
  "booking",
  "mailing_list",
  "music_player",
  "media_upload",
]);

/**
 * The Lissen identity component signs an action token for exactly one
 * newsletter action; the token's context hash is the SHA-256 of this string,
 * computed identically by the page (Web Crypto) and by Platform API (node
 * crypto), so a token minted for one artist page cannot subscribe to another.
 */
export const newsletterIdentityContext = (artistSlug: string) =>
  `newsletter:${artistSlug.trim().toLowerCase()}`;

export const artistMailingListSignupSchema = z.object({
  // Authenticated calls may omit the address: the API resolves the verified
  // account email instead of trusting a browser-supplied identity.
  email: z.string().trim().email().max(254).optional(),
  // Signed by the Lissen identity component (purpose "newsletter"). When
  // present, the email and any verified user come from the token, never from
  // the browser-supplied fields.
  identityToken: z.string().min(1).max(4096).optional(),
  source: artistAudienceSourceSchema,
  marketingConsent: z.literal(true),
  /** Code verification links an earlier join without renewing its consent. */
  intent: z.enum(["join", "link"]).optional(),
  /** Stay updated on this event (an access id): the join also keeps the
   *  signed-in subscriber's request to hear about it. */
  accessId: z.string().min(1).max(36).optional(),
  website: z.string().max(0).optional(),
});

export const artistMailingListSignupResponseSchema = z.object({
  success: z.literal(true),
});

export const artistMediaUploadRequestSchema = z.object({
  filename: z.string().trim().min(1).max(255),
  contentType: z.enum(artistMediaUploadMimeTypes),
  sizeBytes: z.number().int().positive().max(ARTIST_MEDIA_UPLOAD_MAX_BYTES),
});

export const artistMediaUploadTargetSchema = z.object({
  uploadId: z.string().uuid(),
  signedUrl: z.string().url(),
  method: z.literal("PUT"),
  headers: z.object({ "Content-Type": z.enum(artistMediaUploadMimeTypes) }),
  expiresAt: z.string().datetime(),
});

export const artistMediaUploadCompleteResponseSchema = z.object({
  success: z.literal(true),
  uploadId: z.string().uuid(),
});

export const artistLinkPageSchema = z.object({
  slug: artistLinkPageSlugSchema,
  artist: artistLinkPageArtistSchema,
  theme: artistLinkPageThemeSchema.optional(),
  pinnedAccess: artistLinkPagePinnedAccessSchema.nullable().optional(),
  /**
   * The public renderer normally promotes the pinned item out of its section.
   * Music ownership can override this client-side so an unlocked song returns
   * to Music while the edge-cacheable page payload remains viewer-agnostic.
   */
  hidePinnedAccessFromSections: z.boolean().optional(),
  sections: z.array(artistLinkPageSectionSchema),
});

export const artistLinkPageNotFoundSchema = z.object({
  error: z.literal("artist_link_page_not_found"),
  slug: z.string(),
});

export type ArtistLinkPage = z.infer<typeof artistLinkPageSchema>;
export type ArtistLinkPageArtist = z.infer<typeof artistLinkPageArtistSchema>;
export type ArtistLinkPageSection = z.infer<typeof artistLinkPageSectionSchema>;
export type ArtistLinkPageBookingSection = z.infer<
  typeof artistLinkPageBookingSectionSchema
>;
export type ArtistLinkPageMediaUploadSection = z.infer<
  typeof artistLinkPageMediaUploadSectionSchema
>;
export type ArtistLinkPageMailingListSection = z.infer<
  typeof artistLinkPageMailingListSectionSchema
>;
export type ArtistLinkPageClubSection = z.infer<
  typeof artistLinkPageClubSectionSchema
>;
export type ArtistMailingListSignup = z.infer<
  typeof artistMailingListSignupSchema
>;
export type ArtistAudienceSource = z.infer<typeof artistAudienceSourceSchema>;
export type ArtistMediaUploadRequest = z.infer<
  typeof artistMediaUploadRequestSchema
>;
export type ArtistBookingRequest = z.infer<typeof artistBookingRequestSchema>;
/** Section type discriminator of the public link-page payload. */
export type SectionType = ArtistLinkPageSection["type"];
export type ArtistLinkPageHeaderSection = z.infer<
  typeof artistLinkPageHeaderSectionSchema
>;
export type ArtistLinkPageSongsSection = z.infer<
  typeof artistLinkPageSongsSectionSchema
>;
export type ArtistLinkPageConcertsSection = z.infer<
  typeof artistLinkPageConcertsSectionSchema
>;
export type ArtistLinkPageMerchSection = z.infer<
  typeof artistLinkPageMerchSectionSchema
>;
export type ArtistLinkPageMerch = z.infer<typeof artistLinkPageMerchSchema>;
/** An access a card on the page opens: a concert, a music unlock or merch. */
export type ArtistLinkPageOpenableAccess =
  | ArtistLinkPagePinnedAccess
  | ArtistLinkPageMerch;
export type ArtistLinkPageLinksSection = z.infer<
  typeof artistLinkPageLinksSectionSchema
>;
export type ArtistLinkPageCollaboratorSection = z.infer<
  typeof artistLinkPageCollaboratorSectionSchema
>;
export type ArtistLinkPageAccess = z.infer<typeof artistLinkPageAccessSchema>;
export type ArtistLinkPageBundle = z.infer<typeof artistLinkPageBundleSchema>;
export type ArtistLinkPageMusicAccess = z.infer<
  typeof artistLinkPageMusicAccessSchema
>;
export type ArtistLinkPagePinnedAccess = z.infer<
  typeof artistLinkPagePinnedAccessSchema
>;
export type ArtistLinkPageTour = z.infer<typeof artistLinkPageTourSchema>;
export type ArtistLinkPageConcertItem = z.infer<
  typeof artistLinkPageConcertItemSchema
>;
export type ArtistLinkPagePopularSong = z.infer<
  typeof artistLinkPagePopularSongSchema
>;
export type ArtistLinkPageCustomLink = z.infer<
  typeof artistLinkPageCustomLinkSchema
>;
export type ArtistLinkPageAlbum = z.infer<typeof artistLinkPageAlbumSchema>;
export type ArtistLinkPageSongItem = z.infer<
  typeof artistLinkPageSongItemSchema
>;
export type ArtistLinkPageSongPlatformLink = z.infer<
  typeof artistLinkPageSongPlatformLinkSchema
>;
export type ArtistLinkPageSocialLink = z.infer<
  typeof artistLinkPageSocialLinkSchema
>;
export type ArtistLinkPageTheme = z.infer<typeof artistLinkPageThemeSchema>;
export type ArtistLinkPageSectionStyle = z.infer<
  typeof artistLinkPageSectionStyleSchema
>;
export type ArtistLinkPageNotFound = z.infer<
  typeof artistLinkPageNotFoundSchema
>;
